import React, { useEffect, useRef, useState } from "react";
import {
  Upload,
  FileText,
  Trash2,
  Plus,
  Library,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";
import {
  listDocuments,
  createDocument,
  uploadDocumentFile,
  deleteDocument,
} from "../lib/api";

function fmtSize(n) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}
function fmtTime(iso) {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

export default function Archives() {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [pasteMode, setPasteMode] = useState(false);
  const [pasteName, setPasteName] = useState("");
  const [pasteContent, setPasteContent] = useState("");
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      setDocs(await listDocuments());
    } catch {
      toast.error("Failed to load archive");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleFile = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      await uploadDocumentFile(file);
      toast.success(`Docked: ${file.name}`);
      await load();
    } catch (e) {
      toast.error("Upload failed", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally {
      setUploading(false);
    }
  };

  const handlePaste = async () => {
    if (!pasteContent.trim()) {
      toast.error("Content is empty");
      return;
    }
    setUploading(true);
    try {
      await createDocument(pasteName || "Pasted note", pasteContent);
      toast.success("Docked");
      setPasteName("");
      setPasteContent("");
      setPasteMode(false);
      await load();
    } catch (e) {
      toast.error("Save failed", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteDocument(id);
      toast.success("Purged from archive");
      await load();
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-6 py-10">
      <div className="mb-10">
        <div className="text-[10px] uppercase tracking-[0.35em] text-zinc-500 flex items-center gap-2">
          <Library className="h-3 w-3" /> Source of Truth
        </div>
        <h1 className="font-heading text-4xl mt-1">Architect's Archive</h1>
        <div className="text-xs text-zinc-500 mt-2 max-w-2xl leading-relaxed">
          Upload your blueprints, whitepapers, or HSL logs. When the Truth Tether
          is enabled, the Sentinel consults the archive on every query and
          refuses to fabricate answers not grounded in it.
        </div>
      </div>

      {/* Upload + paste row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
        <label
          className="border-2 border-dashed border-zinc-800 hover:border-emerald-500 bg-[#0b0b0d] p-8 flex flex-col items-center justify-center gap-3 cursor-pointer transition-colors"
          data-testid="upload-dropzone"
          onDrop={(e) => {
            e.preventDefault();
            handleFile(e.dataTransfer.files?.[0]);
          }}
          onDragOver={(e) => e.preventDefault()}
        >
          <input
            ref={fileRef}
            type="file"
            accept=".txt,.md,.markdown,.pdf,text/plain,application/pdf"
            className="hidden"
            onChange={(e) => handleFile(e.target.files?.[0])}
            data-testid="upload-file-input"
          />
          {uploading ? (
            <Loader2 className="h-6 w-6 text-emerald-400 animate-spin" />
          ) : (
            <Upload className="h-6 w-6 text-emerald-400" />
          )}
          <div className="text-xs uppercase tracking-[0.3em] text-zinc-300">
            {uploading ? "Docking…" : "Drop file · .txt .md .pdf"}
          </div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-600">
            or click to browse
          </div>
        </label>

        <button
          onClick={() => setPasteMode((v) => !v)}
          data-testid="toggle-paste-btn"
          className="border border-zinc-800 hover:border-emerald-500 bg-[#0b0b0d] p-8 flex flex-col items-center justify-center gap-3 transition-colors"
        >
          <Plus className="h-6 w-6 text-zinc-300" />
          <div className="text-xs uppercase tracking-[0.3em] text-zinc-300">
            {pasteMode ? "Cancel paste" : "Paste text directly"}
          </div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-600">
            quick note · no file
          </div>
        </button>
      </div>

      {pasteMode && (
        <div
          className="border border-zinc-800 bg-[#0b0b0d] p-6 mb-8"
          data-testid="paste-panel"
        >
          <label className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">
            Title
          </label>
          <input
            data-testid="paste-name-input"
            value={pasteName}
            onChange={(e) => setPasteName(e.target.value)}
            placeholder="e.g. Aegis Whitepaper v2"
            className="mt-2 w-full bg-zinc-950 border border-zinc-800 focus:border-emerald-500 focus:outline-none text-xs p-3 font-body text-zinc-100 placeholder:text-zinc-600"
          />
          <label className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mt-5 block">
            Content
          </label>
          <textarea
            data-testid="paste-content-input"
            value={pasteContent}
            onChange={(e) => setPasteContent(e.target.value)}
            rows={10}
            placeholder="Paste the authoritative text here…"
            className="mt-2 w-full bg-zinc-950 border border-zinc-800 focus:border-emerald-500 focus:outline-none text-xs p-3 font-body resize-none text-zinc-100 placeholder:text-zinc-600"
          />
          <div className="mt-4 flex gap-3">
            <button
              onClick={handlePaste}
              disabled={uploading}
              data-testid="save-paste-btn"
              className="inline-flex items-center gap-2 px-6 py-3 border border-emerald-500 text-emerald-400 hover:bg-emerald-500 hover:text-zinc-950 disabled:opacity-50 text-xs uppercase tracking-[0.3em]"
            >
              {uploading ? "Docking…" : "Save · Anchor"}
            </button>
          </div>
        </div>
      )}

      {/* List */}
      <div className="text-[10px] uppercase tracking-[0.35em] text-zinc-500 mb-3">
        Docked · {docs.length} {docs.length === 1 ? "document" : "documents"}
      </div>
      {loading && docs.length === 0 ? (
        <div className="border border-zinc-800 bg-[#0b0b0d] p-12 text-center text-xs uppercase tracking-[0.3em] text-zinc-500">
          Scanning archive…
        </div>
      ) : docs.length === 0 ? (
        <div
          className="border border-zinc-800 bg-[#0b0b0d] p-12 text-center text-xs uppercase tracking-[0.3em] text-zinc-500"
          data-testid="archive-empty"
        >
          No documents yet. Upload your first blueprint to anchor the Sentinel.
        </div>
      ) : (
        <div className="border border-zinc-800" data-testid="archive-list">
          {docs.map((d, i) => (
            <div
              key={d.doc_id}
              data-testid={`doc-row-${d.doc_id}`}
              className={`grid grid-cols-1 md:grid-cols-12 gap-3 md:gap-4 p-4 sm:p-5 ${
                i !== docs.length - 1 ? "border-b border-zinc-900" : ""
              }`}
            >
              <div className="md:col-span-6 flex items-start gap-3">
                <FileText className="h-4 w-4 text-emerald-400 mt-0.5 shrink-0" />
                <div className="min-w-0">
                  <div className="text-sm text-zinc-100 font-body truncate">
                    {d.name}
                  </div>
                  <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-600 mt-1">
                    {d.chunk_count} chunks · {fmtSize(d.size)}
                  </div>
                </div>
              </div>
              <div className="md:col-span-4 text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                <div>docked</div>
                <div className="mt-0.5 normal-case tracking-normal text-zinc-400">
                  {fmtTime(d.uploaded_at)}
                </div>
              </div>
              <div className="md:col-span-2 flex md:justify-end items-center">
                <button
                  onClick={() => handleDelete(d.doc_id)}
                  data-testid={`delete-doc-${d.doc_id}`}
                  className="p-2 border border-zinc-800 hover:border-red-500 hover:text-red-400 text-zinc-500"
                  aria-label="Delete document"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
